import { Box, Center, Flex, Text } from '@chakra-ui/react';
import { useVendor } from '@outdoorly/react-chat-sdk';
import { motion } from 'framer-motion';
import posthog from 'posthog-js';
import React, { useMemo } from 'react';
import { EXPERT_IMAGES } from '../../constants';
import { Sparkles } from '../../icons';
import { Store } from '../../store';
import { useIsDesktop } from '../../utils/dimensions';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import { MessageEvent } from '../Message/MessageEvent';

const RADIUS = 110;
const LAYOUT_WIDTH = 300;
const ANGLE_OFFSET = Math.random();
const NUMBER_OF_EXPERTS = 8;
const EXPERT_SCALE_RANGE = [0.8, 2];
const EXPERT_RANDOM_TRANSLATION_MAX = RADIUS / 9;
const EXPERT_CYCLE_DURATION = 10;
const EXPERT_APPEAR_DURATION = 3.5;
const EXPERT_TRANSITION_DURATION = 0.15;
const EXPERT_BASE_SIZE_PIXELS = 30;
const BRAND_LOGO_SCALE = 1.2;
const PULSE_INTERVAL = 2.5;

const randInRange = (min: number, max: number) => {
  const diff = max - min;
  const rand = Math.random();
  return rand * diff + min;
};

const getExperts = (vendorImages: string[]) =>
  [...Array(NUMBER_OF_EXPERTS)].map((_, index, array) => {
    const angle = ((index + ANGLE_OFFSET) / array.length) * Math.PI * 2;

    const RANDOM_TRANSLATION =
      Math.random() * 2 * EXPERT_RANDOM_TRANSLATION_MAX - EXPERT_RANDOM_TRANSLATION_MAX;

    return {
      scale: randInRange(EXPERT_SCALE_RANGE[0], EXPERT_SCALE_RANGE[1]),
      y: -Math.cos(angle) * RADIUS + LAYOUT_WIDTH / 2 + RANDOM_TRANSLATION,
      x: Math.sin(angle) * RADIUS + LAYOUT_WIDTH / 2 + RANDOM_TRANSLATION,
      image: tryToBuildImgixUrl(vendorImages[index] ?? EXPERT_IMAGES[index], {
        w: 100,
        h: 100,
        auto: 'format,compress',
        crop: 'faces'
      })
    };
  });

export const WaitingForPairing: React.FC<{ scrollToBottomIfClose: () => void; didAILeave: boolean }> = ({
  scrollToBottomIfClose,
  didAILeave
}) => {
  const vendor = useVendor();
  const experts = useMemo(() => getExperts(vendor.vendor?.expertAvatarLineup ?? []), [vendor.vendor]);

  const BRAND_LOGO =
    vendor?.vendor?.logo ||
    'https://images.unsplash.com/photo-1516876902004-79f4bd1cb0dc?fit=crop&w=400&h=400&fit=crop&crop=faces';

  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const labels = Store.useState((s) => s.labels);
  useMemo(() => {
    posthog.capture('FINDING-EXPERT');
  }, []);

  return (
    <Flex
      w="100%"
      h="100%"
      minH={isMobile ? 'calc(100% - 140px - env(safe-area-inset-bottom))' : '514px'}
      flexDir="column"
      justify="space-between"
      pos="relative"
    >
      {didAILeave && (
        <Box
          position="absolute"
          top="21px"
          left="0"
          w="calc(100% - 32px)"
          p="8px"
          mx="16px"
          borderRadius="8px"
          backgroundColor="scheme.purple0"
          border="1px solid rgba(160, 79, 235, 0.20)"
          boxShadow="0px 2px 4px 0px rgba(0, 0, 0, 0.06)"
          zIndex="overlay"
        >
          <Flex gap="8px" align="center">
            <Sparkles color="scheme.purple3" height="17px" width="17px" />
            <Text variant="label-small" color="scheme.purple8">
              {labels.aiChatTransferToastTitle}
            </Text>
          </Flex>
          <Text variant="label-medium" color="scheme.purple7" opacity={0.6} mt="8px">
            {labels.aiChatTransferToastMessage}
          </Text>
        </Box>
      )}
      <Center flexGrow={1}>
        <Box>
          <Box pos="relative" padding="30px" h={LAYOUT_WIDTH} w={LAYOUT_WIDTH} borderRadius="20px">
            <motion.div
              style={{
                position: 'absolute',
                margin: 'auto auto',
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                height: '50px',
                width: '50px',
                border: '13px solid black',
                boxSizing: 'border-box',
                borderRadius: '100px'
              }}
              animate={{
                scale: [1.0, 5.5],
                opacity: [0.2, 0.0],
                filter: ['blur(0px)', 'blur(1px)']
              }}
              transition={{
                repeat: Infinity,
                duration: PULSE_INTERVAL,
                ease: 'easeOut',
                times: [0.25, 0.75]
              }}
            />
            <motion.div
              style={{
                position: 'absolute',
                margin: 'auto auto',
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                height: '50px',
                width: '50px',
                backgroundImage: `url(${BRAND_LOGO})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '50px',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)'
              }}
              animate={{ scale: [1.01, BRAND_LOGO_SCALE, BRAND_LOGO_SCALE, 1.01] }} // Weird bug where scale 1.0 causes background image to be wrong size
              transition={{
                repeat: Infinity,
                duration: PULSE_INTERVAL,
                ease: 'easeInOut',
                times: [0.0, 0.45, 0.55, 1]
              }}
            />
            {experts.map((e, index, array) => (
              <motion.div
                style={{
                  position: 'absolute',
                  x: e.x - (EXPERT_BASE_SIZE_PIXELS * e.scale) / 2,
                  y: e.y - (EXPERT_BASE_SIZE_PIXELS * e.scale) / 2,
                  width: EXPERT_BASE_SIZE_PIXELS * e.scale,
                  height: EXPERT_BASE_SIZE_PIXELS * e.scale,
                  background: `url(${e.image}) no-repeat center center`,
                  borderRadius: '50px',
                  backgroundSize: 'cover',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)'
                }}
                animate={{
                  translateY: [20, 0, 0, -20],
                  opacity: [0, 1, 1, 0]
                }}
                key={index}
                transition={{
                  times: [
                    0,
                    EXPERT_TRANSITION_DURATION / EXPERT_CYCLE_DURATION,
                    (EXPERT_TRANSITION_DURATION + EXPERT_APPEAR_DURATION) / EXPERT_CYCLE_DURATION,
                    (2 * EXPERT_TRANSITION_DURATION + EXPERT_APPEAR_DURATION) / EXPERT_CYCLE_DURATION
                  ],
                  delay: Math.floor(Math.random() * EXPERT_CYCLE_DURATION) + Math.random() * 0.3, // Delay by whole seconds, but add a little jitter
                  repeat: Infinity,
                  duration: EXPERT_CYCLE_DURATION
                }}
              />
            ))}
          </Box>
        </Box>
      </Center>

      <MessageEvent
        eventType="ShowFindingExpert"
        isLastMessage={false}
        scrollToBottomIfClose={scrollToBottomIfClose}
      />
    </Flex>
  );
};
